import type { IPayoutInstructionExternalAccount, VerificationStatus } from '../types/global/payout.types';

const STATUS_CONFIG: Record<
    VerificationStatus,
    { label: string; color: 'success' | 'warning' | 'error' | 'info' | 'default' }
> = {
    verified: { label: 'Verified', color: 'success' },
    pending: { label: 'Pending', color: 'warning' },
    failed: { label: 'Failed', color: 'error' },
    pending_processor: { label: 'Pending setup', color: 'info' },
    pending_manual_review: { label: 'Manual review', color: 'warning' },
    not_applicable: { label: 'Manual payout', color: 'default' },
};

export function verificationStatusDisplay(status?: VerificationStatus | string) {
    if (!status) return { label: 'Unknown', color: 'default' as const };
    return STATUS_CONFIG[status as VerificationStatus] ?? { label: status, color: 'default' as const };
}

export function payoutProcessorLabel(processor?: string | null, stripeRegion?: string | null): string {
    if (!processor) return 'Manual';
    if (processor === 'paystack') return 'Paystack';
    if (processor === 'stripe') return stripeRegion ? `Stripe ${stripeRegion.toUpperCase()}` : 'Stripe';
    return processor;
}

export function sendViaLabel(sendVia: string): string {
    switch (sendVia) {
        case 'paystack':
            return 'Paystack';
        case 'stripe':
            return 'Stripe';
        case 'manual_wire':
            return 'Manual wire';
        case 'paypal':
            return 'PayPal';
        case 'manual_fulfillment':
            return 'Fulfillment';
        case 'unknown':
            return 'Unknown';
        default:
            return sendVia;
    }
}

export function executeNeedsAmountMinor(error: unknown): boolean {
    const msg = String(
        (error as { message?: string })?.message ??
            (error as { data?: { message?: string } })?.data?.message ??
            (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            '',
    ).toLowerCase();
    return msg.includes('amount_minor') || msg.includes('currency mismatch') || msg.includes('account currency');
}

export function sourceTypeLabel(sourceType: string): string {
    return sourceType.replace(/_/g, ' ');
}

export function formatCents(amountCents: number, currency = 'usd'): string {
    const amount = amountCents / 100;
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency.toUpperCase(),
        }).format(amount);
    } catch {
        return `${amount.toFixed(2)} ${currency.toUpperCase()}`;
    }
}

/** Plain-text destination details for clipboard / offline send. */
export function formatPayoutDestinationCopy(
    ext?: IPayoutInstructionExternalAccount | null,
    user?: { email?: string; phoneNumber?: string } | null,
): string {
    if (!ext && !user) return '';
    const lines: string[] = [];
    if (ext?.name) lines.push(`Account holder: ${ext.name}`);
    if (ext?.verified_account_name) lines.push(`Verified name: ${ext.verified_account_name}`);
    if (ext?.country) lines.push(`Country: ${ext.country}`);
    if (ext?.account_currency) lines.push(`Currency: ${ext.account_currency}`);
    if (ext?.external_account_type) lines.push(`Type: ${ext.external_account_type}`);
    if (ext?.processor_recipient_id) lines.push(`Recipient: ${ext.processor_recipient_id}`);
    if (ext?.verification_status) lines.push(`Verification: ${ext.verification_status}`);

    const bank = ext?.bank_details;
    if (bank) {
        if (bank.bank_name) lines.push(`Bank: ${bank.bank_name}`);
        if (bank.bank_code) lines.push(`Bank code: ${bank.bank_code}`);
        if (bank.account_number) lines.push(`Account number: ${bank.account_number}`);
        if (bank.routing_number) lines.push(`Routing / SWIFT: ${bank.routing_number}`);
        if (bank.address) lines.push(`Residential address: ${bank.address}`);
    }

    const momo = ext?.mobile_money_details;
    if (momo) {
        if (momo.mobile_money_name || momo.mobile_money_code) {
            lines.push(`Mobile money: ${momo.mobile_money_name ?? momo.mobile_money_code}`);
        }
        if (momo.phone_number) lines.push(`Phone / number: ${momo.phone_number}`);
        if (momo.name) lines.push(`MoMo name: ${momo.name}`);
    }

    const paypal = ext?.paypal_details;
    if (paypal) {
        if (paypal.email) lines.push(`PayPal email: ${paypal.email}`);
        if (paypal.username) lines.push(`PayPal username: ${paypal.username}`);
    }

    if (user?.email) lines.push(`Profile email: ${user.email}`);
    if (user?.phoneNumber) lines.push(`Profile phone: ${user.phoneNumber}`);

    return lines.join('\n');
}
